// 商业 SKU 管理 — 钻石包 / 会员订阅 / 单次解锁 / 实体加购 的增删改 + 定价.
// 平台商业模块的运营自助入口: 小程序充值弹窗/会员页读的就是这里配的 SKU.
//
// 后端: handlers/commerce/admin.go
//   GET  /admin/v1/commerce/skus?app_id=&kind=
//   POST /admin/v1/commerce/skus
//   PUT  /admin/v1/commerce/skus/:id          (全量字段)
//   POST /admin/v1/commerce/skus/:id/prices   (价格组 × 渠道覆盖价)

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ResourceList } from '@/components/ResourceList';
import { listApps, type App } from '@/api/apps';
import {
  createSKU,
  listPriceGroups,
  listSKUs,
  updateSKU,
  upsertSKUPrice,
  type PriceChannel,
  type PriceGroup,
  type SKU,
  type SKUInput,
  type SKUKind,
  type SKUStatus,
} from '@/api/skus';
import { listCardKeyStats, type CardKeySKUStat } from '@/api/cardkeys';
import type { CatalogSKUPrefill } from '@/api/catalog';
import { formatCNY, formatDateTime } from '@/utils/format';

const KIND_META: Record<SKUKind, { label: string; color: string }> = {
  diamond_pack: { label: '钻石包', color: 'gold' },
  subscription: { label: '会员订阅', color: 'purple' },
  one_time: { label: '单次解锁', color: 'blue' },
  physical_addon: { label: '实体加购', color: 'cyan' },
};

const KIND_OPTIONS = (Object.keys(KIND_META) as SKUKind[]).map((k) => ({
  value: k,
  label: KIND_META[k].label,
}));

const STATUS_META: Record<SKUStatus, { label: string; color: string }> = {
  // draft 的产品语义是「内部档」而非未完成: 前台列表隐藏, 但授予路径
  // (权益包/后台) 仍可用 — 与会员套餐页文案保持一致 (2026-08-11 统一)。
  draft: { label: '内部档', color: 'gold' },
  active: { label: '上架', color: 'green' },
  archived: { label: '已下架', color: 'default' },
};

const STATUS_OPTIONS = (Object.keys(STATUS_META) as SKUStatus[]).map((s) => ({
  value: s,
  label: `${STATUS_META[s].label} (${s})`,
}));

const CHANNEL_OPTIONS: Array<{ value: PriceChannel; label: string }> = [
  { value: 'all', label: '全渠道 (all)' },
  { value: 'wx_miniapp', label: '微信小程序' },
  { value: 'dy_miniapp', label: '抖音小程序' },
  { value: 'h5', label: 'H5' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

// ---- config.storefront / config.fulfillment (h5-benefit-store task 2.2) ----
// 契约见 openspec/changes/h5-benefit-store design D2/D3: 陈列存 SKU config,
// 不建新表; H5 商城 GET /v1/store/items 读 active + storefront.listed=true.
// 键名 camelCase: listed/sort/headline/sellingPoints/badge/coverKey.

interface StorefrontConfig {
  listed?: boolean;
  sort?: number;
  headline?: string;
  sellingPoints?: string[];
  badge?: string;
  coverKey?: string;
}

interface FulfillmentConfig {
  provider?: string;
  params?: Record<string, unknown>;
}

// ---- card-api 履约参数 (config.fulfillment.params.*) — cardkey-supplier-cardapi ----
// stock 挂在 params.stock (与 product_code/card_type/agreed_price 平级), 与后端
// cardsupply.providerParams schema 一致.
interface CardApiStock {
  enabled?: boolean;
  target?: number;
  low_watermark?: number;
  on_depletion?: string;
}
interface CardApiParams {
  product_code?: string;
  card_type?: string;
  agreed_price?: string;
  unit_price?: string;
  recharge_num?: number;
  stock?: CardApiStock;
  [k: string]: unknown;
}

/** card-api SKU 的 params → 表单字段 (仅 provider=card-api 时填充). */
function cardApiFormValues(ff: FulfillmentConfig): Record<string, unknown> {
  if (!isCardApiFamily(ff.provider)) return {};
  const p = (ff.params ?? {}) as CardApiParams;
  const stock = (p.stock ?? {}) as CardApiStock;
  return {
    ff_product_code: p.product_code ?? '',
    ff_card_type: p.card_type ?? '1',
    ff_agreed_price: p.agreed_price ?? '',
    ff_unit_price: p.unit_price ?? '',
    ff_recharge_num: typeof p.recharge_num === 'number' ? p.recharge_num : undefined,
    ff_stock_enabled: Boolean(stock.enabled),
    ff_stock_target: typeof stock.target === 'number' ? stock.target : undefined,
    ff_stock_low_watermark: typeof stock.low_watermark === 'number' ? stock.low_watermark : undefined,
    ff_stock_on_depletion: stock.on_depletion ?? 'fallback_realtime',
  };
}

/** 表单字段 → card-api params (保留已有未知键; stock 嵌在 params 里). */
function buildCardApiParams(
  v: Record<string, unknown>,
  existing: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const params: Record<string, unknown> = { ...(existing ?? {}) };
  params.product_code = String(v.ff_product_code ?? '').trim();
  params.card_type = String(v.ff_card_type ?? '1');
  const agreed = String(v.ff_agreed_price ?? '').trim();
  if (agreed) params.agreed_price = agreed;
  else delete params.agreed_price;
  const unit = String(v.ff_unit_price ?? '').trim();
  if (unit) params.unit_price = unit;
  else delete params.unit_price;
  if (v.ff_recharge_num != null && v.ff_recharge_num !== '') {
    params.recharge_num = Number(v.ff_recharge_num);
  } else {
    delete params.recharge_num;
  }
  const prevStock = (params.stock as CardApiStock | undefined) ?? {};
  const stock: Record<string, unknown> = { ...prevStock };
  stock.enabled = Boolean(v.ff_stock_enabled);
  if (v.ff_stock_target != null && v.ff_stock_target !== '') stock.target = Number(v.ff_stock_target);
  if (v.ff_stock_low_watermark != null && v.ff_stock_low_watermark !== '') {
    stock.low_watermark = Number(v.ff_stock_low_watermark);
  }
  stock.on_depletion = String(v.ff_stock_on_depletion ?? 'fallback_realtime');
  params.stock = stock;
  return params;
}

const FULFILLMENT_PROVIDER_OPTIONS = [
  { value: 'mock', label: 'mock (联调假发货)' },
  { value: 'cardkey', label: 'cardkey (人工导入卡密, 支付后自动发)' },
  { value: 'card-api', label: 'card-api (供应商实时换卡 + 混合库存)' },
  { value: 'card-api-topup', label: 'card-api-topup (供应商直充, 实时到账)' },
];

/**
 * card-api 家族 (实时换卡 card-api / 直充 card-api-topup) 共用结构化履约表单
 * (product_code / 结算价 / 混合库存策略). 从「供应商目录」页「配成 SKU」预填时,
 * 直充 → card-api-topup, 卡密 → card-api.
 */
function isCardApiFamily(p: string | undefined | null): boolean {
  return p === 'card-api' || p === 'card-api-topup';
}

// card-api 卡种 (config.fulfillment.params.card_type).
const CARD_TYPE_OPTIONS = [
  { value: '1', label: '1 · 单卡卡密' },
  { value: '2', label: '2 · 卡号+密码' },
  { value: '3', label: '3 · 网址链接' },
  { value: '4', label: '4 · 卡密+兑换地址' },
];

// 库存耗尽行为 (config.fulfillment.params.stock.on_depletion).
const ON_DEPLETION_OPTIONS = [
  { value: 'fallback_realtime', label: '退回实时换卡 (fallback_realtime, 默认)' },
  { value: 'queue', label: '排队等补货 (queue, 置 pending)' },
  { value: 'sold_out', label: '返回售罄 (sold_out)' },
];

/** 安全取 SKU config 里的子对象 (config 可能为 null / 键可能缺失). */
function configSection<T extends object>(sku: SKU | null, key: string): T {
  const cfg = sku?.config as Record<string, unknown> | null | undefined;
  const section = cfg?.[key];
  return (section && typeof section === 'object' ? section : {}) as T;
}

/** cycle_days → 运营常用叫法. */
function cycleLabel(days: number): string {
  if (days <= 0) return '-';
  if (days === 7) return '周卡 (7天)';
  if (days === 30) return '月卡 (30天)';
  if (days === 365) return '年卡 (365天)';
  return `${days} 天`;
}

export function SKUsListPage() {
  // ---- App 选择器 (SKU 按 App 记账, 后端 list 必须带 app_id) ----
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState<number | undefined>(undefined);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    listApps({ page_size: 100 })
      .then((res) => {
        setApps(res.data);
        // 默认选第一个 active 的 App (没有就第一个)
        const first = res.data.find((a) => a.status === 'active') ?? res.data[0];
        if (first) setAppId((prev) => prev ?? first.id);
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '加载 App 列表失败'));
  }, []);

  const appOptions = useMemo(
    () =>
      apps.map((a) => ({
        value: a.id,
        label: `#${a.id} ${a.name} (${a.slug})`,
      })),
    [apps],
  );

  // ---- 卡密库存 (仅卡密类 SKU 展示; 数据来自 /admin/v1/cardkeys/stats) ----
  const [ckStats, setCkStats] = useState<Record<number, CardKeySKUStat>>({});
  useEffect(() => {
    if (!appId) return;
    listCardKeyStats(appId)
      .then((res) => {
        const byID: Record<number, CardKeySKUStat> = {};
        for (const s of res.data) byID[s.sku_id] = s;
        setCkStats(byID);
      })
      .catch(() => setCkStats({}));
  }, [appId, reloadKey]);

  // ---- 新建 / 编辑 Modal (共用表单; 编辑时后端 PUT 要求全量字段) ----
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<SKU | null>(null); // null = 新建
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const watchedKind = Form.useWatch<SKUKind | undefined>('kind', form);
  const watchedProvider = Form.useWatch<string | undefined>('ff_provider', form);
  const watchedStockEnabled = Form.useWatch<boolean | undefined>('ff_stock_enabled', form);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      kind: 'diamond_pack',
      status: 'active',
      auto_renew: false,
      sf_listed: false,
    });
    setEditOpen(true);
  };

  const openEdit = (row: SKU) => {
    setEditing(row);
    form.resetFields();
    const sf = configSection<StorefrontConfig>(row, 'storefront');
    const ff = configSection<FulfillmentConfig>(row, 'fulfillment');
    form.setFieldsValue({
      code: row.code,
      name: row.name,
      kind: row.kind,
      price_yuan: row.base_price_cents / 100,
      diamond_amount: row.diamond_amount || undefined,
      cycle_days: row.cycle_days || undefined,
      auto_renew: row.auto_renew,
      status: row.status,
      // 商城陈列 (config.storefront)
      sf_listed: Boolean(sf.listed),
      sf_sort: typeof sf.sort === 'number' ? sf.sort : undefined,
      sf_headline: sf.headline ?? '',
      sf_selling_points: Array.isArray(sf.sellingPoints) ? sf.sellingPoints.join('\n') : '',
      sf_badge: sf.badge ?? '',
      sf_cover_key: sf.coverKey ?? '',
      // 履约配置 (config.fulfillment)
      ff_provider: ff.provider || undefined,
      ff_params:
        !isCardApiFamily(ff.provider) && ff.params && Object.keys(ff.params).length > 0
          ? JSON.stringify(ff.params, null, 2)
          : '',
      // card-api 结构化参数 (config.fulfillment.params.*)
      ...cardApiFormValues(ff),
    });
    setEditOpen(true);
  };

  // ---- 「供应商目录」页「配成 SKU」跨页预填 (router state.prefillSKU) ----
  // 打开新建 Modal 并回填 provider / product_code / 面值(→基础价) / 结算价(→agreed_price);
  // 其余字段 (code/name/kind 等) 由运营在 Modal 内补齐. appId 用页面当前所选 App.
  const location = useLocation();
  useEffect(() => {
    const pf = (location.state as { prefillSKU?: CatalogSKUPrefill } | null)?.prefillSKU;
    if (!pf) return;
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      kind: 'one_time',
      status: 'active',
      auto_renew: false,
      sf_listed: false,
      // 面值原样字符串 → 基础价 (元); InputNumber 需 number.
      price_yuan: pf.face_value ? Number(pf.face_value) : undefined,
      ff_provider: pf.provider,
      ff_card_type: '1',
      ff_product_code: pf.product_code,
      // 结算价原样字符串 → 供应商密价保护的 agreed_price.
      ff_agreed_price: pf.settle_price ?? '',
      ff_stock_on_depletion: 'fallback_realtime',
    });
    setEditOpen(true);
    // 清掉 router state, 防止刷新/后退时重复弹窗.
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleSubmit = async () => {
    if (!appId) {
      message.warning('请先选择 App');
      return;
    }
    let v: Record<string, unknown>;
    try {
      v = await form.validateFields();
    } catch {
      return; // 表单校验失败, antd 已高亮
    }
    const kind = v.kind as SKUKind;

    // ---- config: PUT 是全量覆盖 — 以原 config 为底, 只合并 storefront /
    // fulfillment 两个键, 保留 package 等兄弟键不被清空. ----
    const config: Record<string, unknown> = {
      ...((editing?.config as Record<string, unknown> | null) ?? {}),
    };
    const sellingPoints = String(v.sf_selling_points ?? '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    config.storefront = {
      // 同样保底 storefront 内未知键 (未来扩展) 不被表单覆盖丢失.
      ...configSection<Record<string, unknown>>(editing, 'storefront'),
      listed: Boolean(v.sf_listed),
      sort: Number(v.sf_sort ?? 0),
      headline: String(v.sf_headline ?? '').trim(),
      sellingPoints,
      badge: String(v.sf_badge ?? '').trim(),
      coverKey: String(v.sf_cover_key ?? '').trim(),
    };
    const ffProvider = v.ff_provider ? String(v.ff_provider) : '';
    if (ffProvider) {
      const prevFf = configSection<FulfillmentConfig>(editing, 'fulfillment');
      let params: Record<string, unknown>;
      if (isCardApiFamily(ffProvider)) {
        // 结构化表单 → params (stock 嵌在 params 里); 保留原 params 未知键.
        params = buildCardApiParams(v, prevFf.params);
      } else {
        // 其余 provider 走原始 JSON 文本框 (JSON 合法性已由表单 validator 保证).
        const rawParams = String(v.ff_params ?? '').trim();
        params = rawParams ? (JSON.parse(rawParams) as Record<string, unknown>) : {};
      }
      config.fulfillment = {
        ...(prevFf as Record<string, unknown>),
        provider: ffProvider,
        params,
      };
    } else {
      // provider 清空 = 移除履约配置 (支付后不触发发货).
      delete config.fulfillment;
    }

    const input: SKUInput = {
      app_id: editing ? editing.app_id : appId,
      code: String(v.code).trim(),
      name: String(v.name).trim(),
      kind,
      base_price_cents: Math.round(Number(v.price_yuan) * 100),
      diamond_amount:
        kind === 'diamond_pack' || kind === 'subscription' ? Number(v.diamond_amount ?? 0) : 0,
      cycle_days: kind === 'subscription' ? Number(v.cycle_days ?? 0) : 0,
      auto_renew: kind === 'subscription' ? Boolean(v.auto_renew) : false,
      status: v.status as SKUStatus,
      // PUT 是全量覆盖 — 编辑时把原有 package_id 原样带回, 避免被清空.
      package_id: editing?.package_id ?? undefined,
      config,
    };
    setSubmitting(true);
    try {
      if (editing) {
        await updateSKU(editing.id, input);
        message.success(`SKU ${input.code} 已更新`);
      } else {
        await createSKU(input);
        message.success(`SKU ${input.code} 已创建`);
      }
      setEditOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- 渠道定价 Modal (sku_prices: 价格组 × 渠道 覆盖 base price) ----
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceSKU, setPriceSKU] = useState<SKU | null>(null);
  const [priceGroups, setPriceGroups] = useState<PriceGroup[]>([]);
  const [priceSubmitting, setPriceSubmitting] = useState(false);
  const [priceForm] = Form.useForm();

  const openPrice = (row: SKU) => {
    setPriceSKU(row);
    priceForm.resetFields();
    priceForm.setFieldsValue({ channel: 'all' });
    setPriceOpen(true);
    listPriceGroups()
      .then((res) => setPriceGroups(res.data))
      .catch(() => setPriceGroups([]));
  };

  const handlePriceSubmit = async () => {
    if (!priceSKU) return;
    let v: Record<string, unknown>;
    try {
      v = await priceForm.validateFields();
    } catch {
      return;
    }
    setPriceSubmitting(true);
    try {
      await upsertSKUPrice(priceSKU.id, {
        price_group_id: Number(v.price_group_id),
        channel: v.channel as PriceChannel,
        price_cents: Math.round(Number(v.price_yuan) * 100),
      });
      message.success(`已设置 ${priceSKU.code} 的渠道价`);
      setPriceOpen(false);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '设置失败');
    } finally {
      setPriceSubmitting(false);
    }
  };

  const priceGroupOptions = useMemo(
    () =>
      priceGroups
        .filter((g) => !g.app_id || g.app_id === priceSKU?.app_id)
        .map((g) => ({
          value: g.id,
          label: `#${g.id} ${g.name} (${g.code})${g.app_id ? '' : ' · 平台级'}`,
        })),
    [priceGroups, priceSKU],
  );

  return (
    <>
      <Space style={{ marginBottom: 16 }} align="center">
        <Typography.Text strong>App:</Typography.Text>
        <Select
          value={appId}
          options={appOptions}
          onChange={(v) => setAppId(v)}
          style={{ minWidth: 280 }}
          placeholder="选择 App"
          showSearch
          optionFilterProp="label"
        />
      </Space>

      {!appId && (
        <Alert type="info" showIcon message="请选择 App 后查看该 App 的 SKU 列表" style={{ marginBottom: 16 }} />
      )}

      {appId && (
        <ResourceList<SKU>
          key={`${appId}:${reloadKey}`}
          title="商业 SKU"
          description="可售商品单元: 钻石包 / 会员订阅 / 单次解锁 / 实体加购. 小程序充值弹窗、会员页读的就是这里 status=上架 的 SKU. 基础价为兜底, 渠道价按「价格组 × 渠道」覆盖."
          rowKey="id"
          pagination="offset"
          actions={[{ label: '新建 SKU', icon: <PlusOutlined />, onClick: openCreate }]}
          filters={[
            { key: 'kind', label: '类型', type: 'select', options: KIND_OPTIONS },
          ]}
          fetcher={async ({ filters, page, pageSize }) => {
            // 后端一次性返回该 App 全部 SKU (无 page 元数据) — 合成 total 让
            // antd Table 的客户端分页在 >1 页时也能翻.
            const res = await listSKUs({ app_id: appId, kind: filters.kind as SKUKind | undefined });
            return { ...res, page: { page, page_size: pageSize, total: res.data.length } };
          }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 70 },
            { title: 'Code', dataIndex: 'code', render: (v: string) => <Tag>{v}</Tag> },
            { title: '名称', dataIndex: 'name' },
            {
              title: '类型',
              dataIndex: 'kind',
              render: (v: SKUKind) => (
                <Tag color={KIND_META[v]?.color}>{KIND_META[v]?.label ?? v}</Tag>
              ),
            },
            {
              title: '基础价',
              dataIndex: 'base_price_cents',
              align: 'right',
              render: (v: number) => formatCNY(v),
            },
            {
              title: '钻石数',
              dataIndex: 'diamond_amount',
              align: 'right',
              render: (v: number, row) => {
                if (!v) return '-';
                return row.kind === 'subscription' ? `${v} / 周期` : v;
              },
            },
            {
              title: '周期',
              dataIndex: 'cycle_days',
              render: (v: number, row) => (row.kind === 'subscription' ? cycleLabel(v) : '-'),
            },
            {
              title: '自动续费',
              dataIndex: 'auto_renew',
              render: (v: boolean, row) =>
                row.kind === 'subscription' ? (v ? <Tag color="green">是</Tag> : '否') : '-',
            },
            {
              title: '状态',
              dataIndex: 'status',
              render: (v: SKUStatus) => (
                <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.label ?? v}</Tag>
              ),
            },
            {
              title: '商城陈列',
              key: 'storefront',
              render: (_, row) => {
                const sf = configSection<StorefrontConfig>(row, 'storefront');
                return sf.listed ? <Tag color="green">已陈列</Tag> : '-';
              },
            },
            {
              title: '卡密库存',
              key: 'cardkey_stock',
              align: 'right',
              render: (_, row) => {
                const prov = configSection<FulfillmentConfig>(row, 'fulfillment').provider;
                if (prov !== 'cardkey' && prov !== 'card-api') {
                  return '-';
                }
                const stat = ckStats[row.id];
                if (!stat) return <Typography.Text type="secondary">0</Typography.Text>;
                return (
                  <Typography.Text strong type={stat.low_stock ? 'danger' : undefined}>
                    {stat.available}
                  </Typography.Text>
                );
              },
            },
            {
              title: '更新时间',
              dataIndex: 'updated_at',
              render: (v: string) => formatDateTime(v),
            },
            {
              title: '操作',
              key: 'actions',
              width: 140,
              render: (_, row) => (
                <Space>
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openEdit(row)}>
                    编辑
                  </Button>
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openPrice(row)}>
                    渠道价
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      )}

      {/* ---- 新建 / 编辑 ---- */}
      <Modal
        title={editing ? `编辑 SKU #${editing.id}` : '新建 SKU'}
        open={editOpen}
        onOk={handleSubmit}
        confirmLoading={submitting}
        onCancel={() => setEditOpen(false)}
        okText={editing ? '保存' : '创建'}
        forceRender
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label="Code (唯一, 端上按 code 取)"
            rules={[{ required: true, message: '请填 code' }]}
            tooltip={editing ? '改 code 会影响端上引用, 慎改' : undefined}
          >
            <Input placeholder="如 diamond_pack_60 / member_week / unlock_once" />
          </Form.Item>
          <Form.Item name="name" label="名称 (用户可见)" rules={[{ required: true, message: '请填名称' }]}>
            <Input placeholder="如 60 钻石 / 周卡会员 / 单次解锁" />
          </Form.Item>
          <Form.Item name="kind" label="类型" rules={[{ required: true }]}>
            <Select options={KIND_OPTIONS} disabled={!!editing} />
          </Form.Item>
          <Form.Item
            name="price_yuan"
            label="基础价 (元)"
            rules={[
              { required: true, message: '请填价格' },
              {
                validator: (_, val) =>
                  val > 0 ? Promise.resolve() : Promise.reject(new Error('价格必须大于 0')),
              },
            ]}
            tooltip="按分存储 (base_price_cents). 渠道差异价用列表里的「渠道价」配."
          >
            <InputNumber min={0.01} step={0.1} precision={2} style={{ width: '100%' }} addonAfter="元" />
          </Form.Item>

          {(watchedKind === 'diamond_pack' || watchedKind === 'subscription') && (
            <Form.Item
              name="diamond_amount"
              label={watchedKind === 'diamond_pack' ? '钻石数 (购买后充入)' : '每周期赠钻 (0 = 不赠)'}
              rules={
                watchedKind === 'diamond_pack'
                  ? [{ required: true, message: '钻石包必须填钻石数' }]
                  : undefined
              }
            >
              <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter="钻" />
            </Form.Item>
          )}

          {watchedKind === 'subscription' && (
            <>
              <Form.Item
                name="cycle_days"
                label="周期 (天)"
                rules={[{ required: true, message: '订阅必须填周期' }]}
                tooltip="7 = 周卡, 30 = 月卡, 365 = 年卡"
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} addonAfter="天" />
              </Form.Item>
              <Form.Item name="auto_renew" label="自动续费" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}

          <Form.Item name="status" label="状态" rules={[{ required: true }]} tooltip="只有「上架 (active)」的 SKU 端上可见可买">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>

          {/* ---- 商城陈列 (config.storefront) — H5 权益商城 GET /v1/store/items
               只展示 status=active 且这里开了「上架」的 SKU. ---- */}
          <Divider orientation="left" plain>
            商城陈列 (H5 权益商城)
          </Divider>
          <Form.Item
            name="sf_listed"
            label="上架到商城"
            valuePropName="checked"
            tooltip="开 = H5 商城陈列此商品 (还需 SKU 状态为「上架 (active)」). 关 = 商城不展示, 不影响小程序充值/会员页."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="sf_sort" label="排序 (小的在前)">
            <InputNumber precision={0} style={{ width: '100%' }} placeholder="如 10" />
          </Form.Item>
          <Form.Item name="sf_headline" label="主标题 (商城卡片大字)">
            <Input placeholder="如 爱奇艺月卡+写真会员" />
          </Form.Item>
          <Form.Item name="sf_selling_points" label="卖点 (一行一条)">
            <Input.TextArea rows={3} placeholder={'官方直充秒到账\n支持 7 天无理由'} />
          </Form.Item>
          <Form.Item name="sf_badge" label="角标 (如「限时」「热卖」, 留空不显示)">
            <Input placeholder="限时" />
          </Form.Item>
          <Form.Item
            name="sf_cover_key"
            label="封面 — 填完整 URL (营销素材公开地址)"
            tooltip="商城卡片封面图. 直接粘贴完整可公开访问的图片 URL, 如 https://cdn.example.com/store/xxx.jpg"
          >
            <Input placeholder="https://..." />
          </Form.Item>

          {/* ---- 履约配置 (config.fulfillment) — 支付成功后 WF_FULFILLMENT
               按 provider 发货; 卡密类商品选 cardkey 并在「卡密库存」页导码. ---- */}
          <Divider orientation="left" plain>
            履约配置 (支付后发货)
          </Divider>
          <Form.Item
            name="ff_provider"
            label="履约 Provider"
            tooltip="留空 = 不触发发货 (纯钻石/会员类 SKU 留空即可). cardkey = 人工导入卡密, 支付后自动分配一张. card-api = 供应商实时换卡 (可选囤本地库存抗并发)."
          >
            <Select options={FULFILLMENT_PROVIDER_OPTIONS} allowClear placeholder="留空 = 无需发货" />
          </Form.Item>

          {/* card-api 家族结构化参数 (product_code / card_type / 结算价 / 混合库存策略) */}
          {isCardApiFamily(watchedProvider) ? (
            <>
              <Form.Item
                name="ff_product_code"
                label="供应商产品编码 (product_code)"
                rules={[{ required: true, message: '请填 product_code' }]}
                tooltip="9 位 HHT (官方卡) / YHT (自生成卡) + 6 位数字, 从「卡密库存」页「同步商品折扣价」拿."
              >
                <Input placeholder="如 HHT000000" />
              </Form.Item>
              <Form.Item name="ff_card_type" label="卡种 (card_type)" tooltip="供应商返回的卡形态; 默认 1 单卡卡密.">
                <Select options={CARD_TYPE_OPTIONS} placeholder="默认 1 · 单卡卡密" />
              </Form.Item>
              <Form.Item
                name="ff_agreed_price"
                label="结算总价 agreed_price (元, 单价×数量)"
                tooltip="首选. 供应商密价保护 — 必须与折后价一致, 否则下单被拒. 与折后单价核对 (同步商品页)."
                rules={[
                  {
                    validator: (_, val) => {
                      const agreed = String(val ?? '').trim();
                      const unit = String(form.getFieldValue('ff_unit_price') ?? '').trim();
                      if (!agreed && !unit) {
                        return Promise.reject(new Error('agreed_price 与 unit_price 至少填一个'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="如 0.1" />
              </Form.Item>
              <Form.Item
                name="ff_unit_price"
                label="结算单价 unit_price (元, 可选)"
                tooltip="agreed_price 缺省时的兜底 (仅数量=1 有效)."
              >
                <Input placeholder="如 0.1" />
              </Form.Item>
              <Form.Item
                name="ff_recharge_num"
                label="单次购买数量 recharge_num (可选, 默认 1)"
                tooltip="测试环境上限 3; 生产上限联调确认."
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="1" />
              </Form.Item>

              <Divider orientation="left" plain>
                混合库存策略 (config.fulfillment.params.stock)
              </Divider>
              <Form.Item
                name="ff_stock_enabled"
                label="囤本地库存"
                valuePropName="checked"
                tooltip="关 = 每单实时向供应商换卡 (慢车道). 开 = 先从本地囤货 SKIP LOCKED 出库 (快车道抗并发), 补货 job 维持水位."
              >
                <Switch />
              </Form.Item>
              {watchedStockEnabled && (
                <>
                  <Form.Item
                    name="ff_stock_target"
                    label="目标囤货量 target"
                    tooltip="补货 job 把可用库存补至此值."
                    rules={[{ required: true, message: '开囤库存需填目标囤货量' }]}
                  >
                    <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="如 500" />
                  </Form.Item>
                  <Form.Item
                    name="ff_stock_low_watermark"
                    label="低水位补货线 low_watermark"
                    tooltip="可用库存跌破此值触发补货."
                    rules={[{ required: true, message: '开囤库存需填低水位补货线' }]}
                  >
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="如 100" />
                  </Form.Item>
                  <Form.Item
                    name="ff_stock_on_depletion"
                    label="耗尽行为 on_depletion"
                    tooltip="本地库存被打空后的降级方式."
                  >
                    <Select options={ON_DEPLETION_OPTIONS} placeholder="默认 fallback_realtime" />
                  </Form.Item>
                </>
              )}
            </>
          ) : (
            <Form.Item
              name="ff_params"
              label="Provider 参数 (JSON, 通常留空)"
              rules={[
                {
                  validator: (_, val) => {
                    const raw = String(val ?? '').trim();
                    if (!raw) return Promise.resolve();
                    try {
                      const parsed = JSON.parse(raw);
                      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                        return Promise.reject(new Error('必须是 JSON 对象, 如 {"key":"value"}'));
                      }
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(new Error('JSON 格式不合法'));
                    }
                  },
                },
              ]}
            >
              <Input.TextArea rows={3} placeholder='{}' style={{ fontFamily: 'monospace' }} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* ---- 渠道定价 ---- */}
      <Modal
        title={priceSKU ? `渠道定价 — ${priceSKU.name} (${priceSKU.code})` : '渠道定价'}
        open={priceOpen}
        onOk={handlePriceSubmit}
        confirmLoading={priceSubmitting}
        onCancel={() => setPriceOpen(false)}
        okText="保存"
        forceRender
        width={480}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`基础价 ${priceSKU ? formatCNY(priceSKU.base_price_cents) : '-'} 为兜底; 这里按「价格组 × 渠道」设覆盖价 (同组同渠道重复保存 = 更新).`}
        />
        <Form form={priceForm} layout="vertical">
          <Form.Item
            name="price_group_id"
            label="价格组"
            rules={[{ required: true, message: '请选价格组' }]}
          >
            <Select
              options={priceGroupOptions}
              placeholder={priceGroupOptions.length ? '选择价格组' : '暂无价格组 (需先建 price group)'}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="channel" label="渠道" rules={[{ required: true }]}>
            <Select options={CHANNEL_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="price_yuan"
            label="价格 (元)"
            rules={[
              { required: true, message: '请填价格' },
              {
                validator: (_, val) =>
                  val > 0 ? Promise.resolve() : Promise.reject(new Error('价格必须大于 0')),
              },
            ]}
          >
            <InputNumber min={0.01} step={0.1} precision={2} style={{ width: '100%' }} addonAfter="元" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
